import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SaleItemService } from '../sale-item.service';
import { SnackBarService } from 'src/app/common/components/snackbar/snackbar.service';
import { SnackBarData } from 'src/app/common/components/snackbar/model/snackbar.model';
import { ConfirmDialogService } from 'src/app/common/components/confirm-dialog/confirm-dialog.service';
import { ConfirmDialogModel } from 'src/app/common/components/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-sale-items-popup',
  templateUrl: './group-item-list-popup.component.html',
  styleUrls: ['./group-item-list-popup.component.scss'],
  standalone: false
})
export class GroupItemListPopupComponent implements OnInit {
  displayedColumns: string[] = ['sl', 'code', 'name', 'shortName', 'displayOrder', 'actions'];
  saleItems: any[] = [];
  isLoading = false;
  originalItems: any[] = [];

  constructor(
    public dialogRef: MatDialogRef<GroupItemListPopupComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { groupItemId: number, groupName: string },
    private saleItemService: SaleItemService,
    private snackBarService: SnackBarService,
    private confirmDialogService: ConfirmDialogService
  ) { }

  ngOnInit(): void {
    this.loadSaleItems();
  }

  loadSaleItems(): void {
    this.isLoading = true;

    const requestData = {
      search: {
        filters: {
          simpleSearch: [],
          advSearch: [
            {
              column: 'sale_item.group_item_id',
              operator: '=',
              value: this.data.groupItemId
            }
          ],
          scope: {
            limit: 100,
            offset: 0
          }
        },
        sort: [
          {
            column: 'sale_item.code',
            order: 'asc'
          }
        ]
      }
    };

    this.saleItemService.getList(requestData).subscribe({
      next: (response) => {
        if (response.status === 'SUCCESS') {
          this.saleItems = response.data.items.map((item: any, index: number) => ({
            ...item,
            sl: index + 1,
            shortName: item.short_name || '',
            displayOrder: item.group_display_order // Default display order
          }));
          this.originalItems = JSON.parse(JSON.stringify(this.saleItems));
        } else {
          let snackBarData: SnackBarData = {
            message: response.message,
            title: 'Failed!!!',
            type: 'error'
          };
          this.snackBarService.openSnackBar(snackBarData);
        }
        this.isLoading = false;
      },
      error: (error) => {
        let snackBarData: SnackBarData = {
          message: 'Failed to load sale items. Connection to the server failed.',
          title: 'Failed!!!',
          type: 'error'
        };
        this.snackBarService.openSnackBar(snackBarData);
        this.isLoading = false;
      }
    });
  }

  onSubmit(): void {
    // Check if there are any changes
    const hasChanges = this.saleItems.some((item, index) =>
      item.displayOrder !== this.originalItems[index]?.displayOrder ||
      item.shortName !== this.originalItems[index]?.shortName
    );

    if (hasChanges) {
      // Format data for API call
      const groupItemArray = this.saleItems.map(item => ({
        saleItemId: item.id,
        groupItemId: this.data.groupItemId,
        groupShortName: item.shortName,
        groupDisplayOrder: item.displayOrder
      }));

      // Call API to update group item data
      this.saleItemService.updateGroupItemOrder(groupItemArray).subscribe({
        next: (response) => {
          if (response.status === 'SUCCESS') {
            let snackBarData: SnackBarData = {
              message: 'Updated successfully',
              title: 'Success',
              type: 'success'
            };
            this.snackBarService.openSnackBar(snackBarData);
            this.dialogRef.close(this.saleItems);
          } else {
            let snackBarData: SnackBarData = {
              message: response.message || 'Failed to update group item data',
              title: 'Failed!!!',
              type: 'error'
            };
            this.snackBarService.openSnackBar(snackBarData);
          }
        },
        error: (error) => {
          let snackBarData: SnackBarData = {
            message: 'Failed to update group item data. Connection to the server failed.',
            title: 'Failed!!!',
            type: 'error'
          };
          this.snackBarService.openSnackBar(snackBarData);
        }
      });
    } else {
      // No changes, just close the dialog
      this.dialogRef.close(this.saleItems);
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onDeleteItem(item: any): void {
    const dialogData = new ConfirmDialogModel(
      'Confirm Delete',
      `Are you sure you want to delete "${item.name}" from this group?`
    );

    this.confirmDialogService.openDialog(dialogData)
      .then((confirmed: boolean) => {
        if (confirmed) {
          this.deleteItem(item);
        }
      });
  }

  private deleteItem(item: any): void {
    this.isLoading = true;

    // Create the item object in the format expected by the service
    const saleItemToDelete = {
      id: item.id,
      name: item.name,
      code: item.code
    };

    this.saleItemService.delete(saleItemToDelete).subscribe({
      next: (response) => {
        if (response.status === 'SUCCESS') {
          // Remove the item from the local array
          this.saleItems = this.saleItems.filter(saleItem => saleItem.id !== item.id);

          // Update the serial numbers
          this.saleItems = this.saleItems.map((saleItem, index) => ({
            ...saleItem,
            sl: index + 1
          }));

          // Update the original items array
          this.originalItems = JSON.parse(JSON.stringify(this.saleItems));

          let snackBarData: SnackBarData = {
            message: `"${item.name}" has been successfully deleted`,
            title: 'Success',
            type: 'success'
          };
          this.snackBarService.openSnackBar(snackBarData);
        } else {
          let snackBarData: SnackBarData = {
            message: response.message || 'Failed to delete item',
            title: 'Failed!!!',
            type: 'error'
          };
          this.snackBarService.openSnackBar(snackBarData);
        }
        this.isLoading = false;
      },
      error: (error) => {
        let snackBarData: SnackBarData = {
          message: 'Failed to delete item. Connection to the server failed.',
          title: 'Failed!!!',
          type: 'error'
        };
        this.snackBarService.openSnackBar(snackBarData);
        this.isLoading = false;
      }
    });
  }
}
